import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "@/components/site/Sections";
import { Field, SelectField, FileField } from "@/components/site/FormControls";
import { submitFinancing } from "@/lib/api";
import { FINANCING_DISCLAIMER } from "@/data/content";
import { CheckCircle2, Loader2, Info } from "lucide-react";

const PROP_TYPES = ["Single Family", "Duplex", "Triplex", "Fourplex", "Multifamily", "Mixed Use", "Commercial", "Land", "Other"];
const TXN_TYPES = ["Purchase", "Auction Purchase", "Refinance", "Cash-Out Refinance", "Fix & Flip", "Buy & Hold", "Portfolio", "Other"];
const CREDIT = ["Below 600", "600–639", "640–679", "680–719", "720+", "Prefer Not to Say"];
const EXPERIENCE = ["First Investment Property", "1–3 Deals", "4–10 Deals", "10+ Deals"];
const OCCUPIED = ["Yes", "No", "Unknown"];
const FIN_NEEDED = ["DSCR", "Hard Money", "Fix & Flip", "Bridge", "Rental", "Portfolio", "Refinance", "Not Sure"];

const initial = {
  first_name: "", last_name: "", company: "", phone: "", email: "", state_of_residence: "",
  property_address: "", property_city: "", property_state: "", property_county: "",
  property_type: "", transaction_type: "", purchase_price: "", current_value: "", arv: "",
  rehab: "", monthly_rent: "", loan_amount: "", credit_range: "", experience: "",
  occupied: "Unknown", closing_date: "", financing_needed: "",
};

export default function RequestFinancing() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitFinancing(form);
      setDone(true);
      toast.success("Financing request received — we'll review your deal.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div>
        <PageHero eyebrow="Submitted" title="Financing Request Received." />
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-xl px-4 text-center lg:px-8">
            <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              A financing coordinator will review your deal and follow up. Financing approval is never guaranteed.
            </p>
            <a href="/" className="mt-8 inline-block rounded-full border border-gold/60 px-6 py-3 text-sm font-semibold text-gold hover:bg-gold/10">Back to Home</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        eyebrow="Request Financing Support"
        title="Review My Deal."
        subtitle="Share your deal and we'll organize the transaction and identify potential financing categories. We help coordinate financing — we are not a bank or direct lender."
      />

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <form data-testid="request-financing-form" onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-10">
            <h3 className="font-mono-x text-xs uppercase tracking-widest text-gold">Investor</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="First Name" name="first_name" value={form.first_name} onChange={set} required half testid="rf-first-name" />
              <Field label="Last Name" name="last_name" value={form.last_name} onChange={set} required half testid="rf-last-name" />
              <Field label="Company Name" name="company" value={form.company} onChange={set} half testid="rf-company" />
              <Field label="State of Residence" name="state_of_residence" value={form.state_of_residence} onChange={set} half testid="rf-residence" />
              <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={set} required half testid="rf-phone" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={set} required half testid="rf-email" />
            </div>

            <h3 className="mt-8 font-mono-x text-xs uppercase tracking-widest text-gold">Property</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="Property Address" name="property_address" value={form.property_address} onChange={set} testid="rf-address" />
              <Field label="Property City" name="property_city" value={form.property_city} onChange={set} half testid="rf-city" />
              <Field label="Property State" name="property_state" value={form.property_state} onChange={set} half testid="rf-state" />
              <Field label="Property County" name="property_county" value={form.property_county} onChange={set} half testid="rf-county" />
              <SelectField label="Property Type" name="property_type" value={form.property_type} onChange={set} options={PROP_TYPES} half testid="rf-prop-type" />
              <SelectField label="Transaction Type" name="transaction_type" value={form.transaction_type} onChange={set} options={TXN_TYPES} half testid="rf-txn-type" />
              <SelectField label="Property Occupied?" name="occupied" value={form.occupied} onChange={set} options={OCCUPIED} half testid="rf-occupied" />
              <Field label="Auction / Closing Date" name="closing_date" type="date" value={form.closing_date} onChange={set} half testid="rf-closing" />
            </div>

            <h3 className="mt-8 font-mono-x text-xs uppercase tracking-widest text-gold">Numbers</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="Purchase Price" name="purchase_price" value={form.purchase_price} onChange={set} half testid="rf-purchase" />
              <Field label="Current Property Value" name="current_value" value={form.current_value} onChange={set} half testid="rf-value" />
              <Field label="Estimated ARV" name="arv" value={form.arv} onChange={set} half testid="rf-arv" />
              <Field label="Estimated Rehab" name="rehab" value={form.rehab} onChange={set} half testid="rf-rehab" />
              <Field label="Expected Monthly Rent" name="monthly_rent" value={form.monthly_rent} onChange={set} half testid="rf-rent" />
              <Field label="Requested Loan Amount" name="loan_amount" value={form.loan_amount} onChange={set} half testid="rf-loan" />
              <SelectField label="Credit Score Range" name="credit_range" value={form.credit_range} onChange={set} options={CREDIT} half testid="rf-credit" />
              <SelectField label="Investor Experience" name="experience" value={form.experience} onChange={set} options={EXPERIENCE} half testid="rf-experience" />
              <SelectField label="Financing Needed" name="financing_needed" value={form.financing_needed} onChange={set} options={FIN_NEEDED} required half testid="rf-financing-type" />
            </div>

            <div className="mt-6">
              <FileField label="Attach Documents (optional)" testid="rf-files" onChange={() => toast.info("Files noted. A coordinator will request secure uploads.")} />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="request-financing-form-submit-btn"
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-sm font-semibold text-[#0B132B] transition-all hover:bg-[#E5C158] disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Submitting…" : "Request Financing Review"}
            </button>

            <div className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-slate-500">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{FINANCING_DISCLAIMER}</p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
